import type { RgbaColor, CanvasContextLike } from '@hcie/shared';

/**
 * @file flood_fill.ts
 * @description Image Flood Fill Tool using an optimized stack-based implementation.
 * Refactored to follow AGENTS.md descriptive naming standards.
 */

export interface FillTolerance {
    red: number;
    green: number;
    blue: number;
    alpha: number;
}

/**
 * @function floodFill
 * @description Performs an area fill on the target canvas.
 * @param {number} x - Start X coordinate.
 * @param {number} y - Start Y coordinate.
 * @param {CanvasContextLike} targetCtx - The context to draw into.
 * @param {RgbaColor} fillColor - The color to fill with.
 * @param {FillTolerance} tolerance - Sensitivity for color matching.
 * @param {CanvasContextLike | null} sourceCtx - Optional context to sample colors from.
 * @param {Uint8ClampedArray | null} maskData - Optional selection mask.
 */
export const floodFill = (
    x: number,
    y: number,
    targetCtx: CanvasContextLike,
    fillColor: RgbaColor,
    tolerance: FillTolerance = { red: 32, green: 32, blue: 32, alpha: 32 },
    sourceCtx: CanvasContextLike | null = null,
    maskData: Uint8ClampedArray | null = null
) => {
    const canvas = targetCtx.canvas;
    const width: number = canvas.width;
    const height: number = canvas.height;

    const effectiveSourceCtx = sourceCtx || targetCtx;
    const startX: number = Math.floor(x);
    const startY: number = Math.floor(y);

    if (startX < 0 || startX >= width || startY < 0 || startY >= height) return;

    // Check selection mask
    if (maskData) {
        const maskIndex: number = (startY * width + startX) * 4 + 3;
        if (maskData[maskIndex] < 10) {
            console.log("[FloodFill] Start point is outside selection mask.");
            return;
        }
    }

    const sourceData: Uint8ClampedArray = effectiveSourceCtx.getImageData(0, 0, width, height).data;
    const targetImageData: ImageData = targetCtx.getImageData(0, 0, width, height);
    const targetData: Uint8ClampedArray = targetImageData.data;
    
    const startIndex: number = (startY * width + startX) * 4;
    const startRed: number = sourceData[startIndex];
    const startGreen: number = sourceData[startIndex + 1];
    const startBlue: number = sourceData[startIndex + 2];
    const startAlpha: number = sourceData[startIndex + 3];

    // Check if similarity is already perfect
    const isAlreadyFilled: boolean = isColorSimilar(
        startRed, startGreen, startBlue, startAlpha,
        fillColor.red, fillColor.green, fillColor.blue, fillColor.alpha,
        { red: 1, green: 1, blue: 1, alpha: 1 }
    );

    if (isAlreadyFilled) return;

    const stack: number[] = [startY * width + startX];
    const visited: Uint8ClampedArray = new Uint8ClampedArray(width * height);
    visited[startY * width + startX] = 1;

    const isMasked = (idx: number): boolean => {
        if (!maskData) return true;
        return maskData[idx * 4 + 3] > 10;
    };

    while (stack.length > 0) {
        const pixelIdx: number = stack.pop()!;
        const cy: number = Math.floor(pixelIdx / width);
        const cx: number = pixelIdx % width;

        const dataIdx: number = pixelIdx * 4;
        
        targetData[dataIdx] = fillColor.red;
        targetData[dataIdx + 1] = fillColor.green;
        targetData[dataIdx + 2] = fillColor.blue;
        targetData[dataIdx + 3] = fillColor.alpha;

        // Neighbors (N, S, W, E)
        const checkNeighbor = (nx: number, ny: number) => {
            const nIdx: number = ny * width + nx;
            if (visited[nIdx] === 0 && isMasked(nIdx)) {
                const i: number = nIdx * 4;
                const isSimilar: boolean = isColorSimilar(
                    sourceData[i], sourceData[i+1], sourceData[i+2], sourceData[i+3],
                    startRed, startGreen, startBlue, startAlpha,
                    tolerance
                );
                if (isSimilar) {
                    visited[nIdx] = 1;
                    stack.push(nIdx);
                }
            }
        };

        if (cy > 0) checkNeighbor(cx, cy - 1);
        if (cy < height - 1) checkNeighbor(cx, cy + 1);
        if (cx > 0) checkNeighbor(cx - 1, cy);
        if (cx < width - 1) checkNeighbor(cx + 1, cy);
    }

    targetCtx.putImageData(targetImageData, 0, 0);
};

/**
 * @function isColorSimilar
 * @description Helper to check if two colors are similar within a tolerance range.
 */
export const isColorSimilar = (
    red1: number, green1: number, blue1: number, alpha1: number,
    red2: number, green2: number, blue2: number, alpha2: number,
    tolerance: FillTolerance
): boolean => {
    return (
        Math.abs(red1 - red2) <= tolerance.red &&
        Math.abs(green1 - green2) <= tolerance.green &&
        Math.abs(blue1 - blue2) <= tolerance.blue &&
        Math.abs(alpha1 - alpha2) <= tolerance.alpha
    );
};

// Global Exposure
(window as any).isColorSimilar = isColorSimilar;
(window as any).floodFill = floodFill;
